import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert, Badge, Modal, Spinner } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FaSave, FaEye, FaTrash } from 'react-icons/fa';

const EditBlogPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    // Blog state
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [validationErrors, setValidationErrors] = useState({});

    // Form state - CreateBlogPage ile aynı
    const [formData, setFormData] = useState({
        title: '',
        category: '',
        tags: '',
        status: 'draft',
        isFeatured: false,
        isSticky: false
    });

    // Content state for contentEditable - CreateBlogPage ile aynı
    const [content, setContent] = useState('');

    // Modal states
    const [showPreview, setShowPreview] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const categories = [
        { value: 'otomobil-dunyasi', label: 'Otomobil Dünyası' },
        { value: 'arac-bakimi', label: 'Araç Bakımı ve Onarımı' },
        { value: 'otomobil-sozlugu', label: 'Otomobil Sözlüğü' },
        { value: 'dunden-bugune', label: 'Dünden Bugüne' },
        { value: 'finansman-ipuclari', label: 'Finansman İpuçları' },
        { value: 'satış-tavsiyeleri', label: 'Satış Tavsiyeleri' },
        { value: 'sektor-haberleri', label: 'Sektör Haberleri' }
    ];

    // Blog verisini fetch et
    useEffect(() => {
        const fetchBlog = async () => {
            if (!id || !currentUser) return;

            try {
                setLoading(true);
                const response = await fetch(`/api/blogs/admin/${id}`, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });
                const data = await response.json();

                if (data.success) {
                    const blogData = data.data;
                    setBlog(blogData);

                    // Form data'yı populate et
                    setFormData({
                        title: blogData.title || '',
                        category: blogData.category || '',
                        tags: blogData.tags ? blogData.tags.join(', ') : '',
                        status: blogData.status || 'draft',
                        isFeatured: Boolean(blogData.isFeatured),
                        isSticky: Boolean(blogData.isSticky)
                    });

                    // Content'i set et
                    setContent(blogData.content || '');
                } else {
                    setError(data.message || 'Blog yüklenemedi');
                }
            } catch (err) {
                console.error('Blog fetch error:', err);
                setError('Blog yüklenirken hata oluştu');
            } finally {
                setLoading(false);
            }
        };

        fetchBlog();
    }, [id, currentUser]);

    // Permission check
    const hasEditPermission = blog && currentUser && (
        currentUser.userType === 'admin' ||
        (currentUser.userType === 'representative' && blog.author._id === currentUser._id)
    );

    // Form input change - CreateBlogPage ile aynı
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (validationErrors[name]) {
            setValidationErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Image upload handler - CreateBlogPage ile AYNI
    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (file) {
            try {
                const formData = new FormData();
                formData.append('images', file);

                const response = await fetch('/api/blogs/admin/upload-image', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: formData
                });

                const data = await response.json();
                if (data.success) {
                    const img = `<img src="${data.imageUrl}" alt="Content image" style="max-width: 100%; height: auto; margin: 10px 0;" />`;
                    document.execCommand('insertHTML', false, img);
                }
            } catch (error) {
                console.error('Image upload error:', error);
            }
        }
    };

    // Form validation - CreateBlogPage ile aynı
    const validateForm = () => {
        const errors = {};

        if (!formData.title.trim()) {
            errors.title = 'Başlık gereklidir';
        } else if (formData.title.length < 5) {
            errors.title = 'Başlık en az 5 karakter olmalıdır';
        }

        if (!content.trim()) {
            errors.content = 'İçerik gereklidir';
        }

        if (!formData.category) {
            errors.category = 'Kategori seçmelisiniz';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Form submission - Update endpoint'i kullan
    const handleSubmit = async (e, isDraft = false) => {
        e.preventDefault();

        if (!validateForm()) {
            setError('Lütfen form hatalarını düzeltin');
            return;
        }

        setSaving(true);
        setError('');

        try {
            const formDataToSend = new FormData();

            formDataToSend.append('title', formData.title);
            formDataToSend.append('content', content);
            formDataToSend.append('category', formData.category);
            formDataToSend.append('tags', formData.tags);
            formDataToSend.append('status', isDraft ? 'draft' : formData.status);

            if (currentUser.userType === 'admin') {
                formDataToSend.append('isFeatured', formData.isFeatured);
                formDataToSend.append('isSticky', formData.isSticky);
            }

            // Featured Image Upload
            const fileInput = document.querySelector('input[name="images"]');
            if (fileInput && fileInput.files[0]) {
                formDataToSend.append('images', fileInput.files[0]);
            }

            const response = await fetch(`/api/blogs/admin/update/${id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: formDataToSend
            });

            const data = await response.json();

            if (data.success) {
                setSuccess('Blog yazısı başarıyla güncellendi!');
                setTimeout(() => {
                    navigate(`/blog/${data.data.slug}`);
                }, 2000);
            } else {
                setError(data.message || 'Blog güncellenirken hata oluştu');
            }

        } catch (err) {
            console.error('Blog update error:', err);
            setError('Bağlantı hatası oluştu');
        } finally {
            setSaving(false);
        }
    };

    // Blog silme
    const handleDelete = async () => {
        setSaving(true);
        try {
            const response = await fetch(`/api/blogs/admin/delete/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            const data = await response.json();

            if (data.success) {
                setSuccess('Blog yazısı başarıyla silindi!');
                setTimeout(() => {
                    navigate('/blog');
                }, 1500);
            } else {
                setError(data.message || 'Blog silinirken hata oluştu');
            }
        } catch (err) {
            console.error('Blog delete error:', err);
            setError('Bağlantı hatası oluştu');
        } finally {
            setSaving(false);
            setShowDeleteConfirm(false);
        }
    };

    // Preview modal
    const PreviewModal = () => (
        <Modal size="lg" show={showPreview} onHide={() => setShowPreview(false)}>
            <Modal.Header closeButton>
                <Modal.Title>Blog Önizleme</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="blog-preview">
                    <div className="mb-2">
                        <Badge bg="primary">{categories.find(c => c.value === formData.category)?.label}</Badge>
                        {formData.isFeatured && <Badge bg="warning" className="ms-2">Öne Çıkan</Badge>}
                        {formData.isSticky && <Badge bg="info" className="ms-2">Sabit</Badge>}
                    </div>
                    <h2>{formData.title}</h2>
                    <div
                        className="blog-content-preview"
                        dangerouslySetInnerHTML={{ __html: content || '' }}
                    />
                    {formData.tags && (
                        <div className="mt-3">
                            <small className="text-muted">
                                Etiketler: {formData.tags}
                            </small>
                        </div>
                    )}
                </div>
            </Modal.Body>
        </Modal>
    );

    // Delete confirmation modal
    const DeleteConfirmModal = () => (
        <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)}>
            <Modal.Header closeButton>
                <Modal.Title>Blog Yazısını Sil</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>Bu blog yazısını silmek istediğinizden emin misiniz?</p>
                <p><strong>"{formData.title}"</strong></p>
                <Alert variant="warning">
                    <strong>Dikkat:</strong> Bu işlem geri alınamaz!
                </Alert>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
                    İptal
                </Button>
                <Button
                    variant="danger"
                    onClick={handleDelete}
                    disabled={saving}
                >
                    {saving ? 'Siliniyor...' : 'Evet, Sil'}
                </Button>
            </Modal.Footer>
        </Modal>
    );

    // Loading state
    if (loading) {
        return (
            <Container className="py-5">
                <div className="text-center">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2">Blog yükleniyor...</p>
                </div>
            </Container>
        );
    }

    // Permission check
    if (!hasEditPermission) {
        return (
            <Container className="py-5">
                <Alert variant="danger" className="text-center">
                    Bu blogu düzenleme yetkiniz bulunmamaktadır.
                </Alert>
            </Container>
        );
    }

    return (
        <div className="edit-blog-page">
            <Container className="py-5">
                <Row className="justify-content-center">
                    <Col lg={10}>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h1>Blog Yazısını Düzenle</h1>
                            <div>
                                <Button
                                    variant="outline-danger"
                                    className="me-2"
                                    onClick={() => setShowDeleteConfirm(true)}
                                >
                                    <FaTrash className="me-2" />
                                    Sil
                                </Button>
                                <Button
                                    variant="outline-dark"
                                    onClick={() => navigate('/blog')}
                                >
                                    ← Geri Dön
                                </Button>
                            </div>
                        </div>

                        {error && <Alert variant="danger">{error}</Alert>}
                        {success && <Alert variant="success">{success}</Alert>}

                        <Card>
                            <Card.Body>
                                <Form onSubmit={(e) => handleSubmit(e, false)}>
                                    <Row>
                                        {/* Sol Kolon - Ana Form */}
                                        <Col lg={8}>
                                            {/* Başlık */}
                                            <Form.Group className="mb-4">
                                                <Form.Label>Başlık *</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="title"
                                                    value={formData.title}
                                                    onChange={handleInputChange}
                                                    placeholder="Blog yazısının başlığını girin..."
                                                    isInvalid={!!validationErrors.title}
                                                    maxLength={200}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {validationErrors.title}
                                                </Form.Control.Feedback>
                                                <Form.Text className="text-muted">
                                                    {formData.title.length}/200 karakter
                                                </Form.Text>
                                            </Form.Group>

                                            {/* ContentEditable Editor - CreateBlogPage ile aynı */}
                                            <Form.Group className="mb-4">
                                                <Form.Label>İçerik *</Form.Label>
                                                {validationErrors.content && (
                                                    <div className="text-danger mb-2">
                                                        {validationErrors.content}
                                                    </div>
                                                )}

                                                <div className="editor-toolbar mb-2" style={{ border: '1px solid #dee2e6', padding: '0.5rem', borderRadius: '0.375rem 0.375rem 0 0' }}>
                                                    <button type="button" onClick={() => document.execCommand('formatBlock', false, 'p')} style={{ marginRight: '0.5rem', padding: '0.25rem 0.5rem' }}>P</button>
                                                    <button type="button" onClick={() => document.execCommand('formatBlock', false, 'h1')} style={{ marginRight: '0.5rem', padding: '0.25rem 0.5rem' }}>H1</button>
                                                    <button type="button" onClick={() => document.execCommand('formatBlock', false, 'h2')} style={{ marginRight: '0.5rem', padding: '0.25rem 0.5rem' }}>H2</button>
                                                    <button type="button" onClick={() => document.execCommand('bold')} style={{ marginRight: '0.5rem', padding: '0.25rem 0.5rem' }}>B</button>
                                                    <button type="button" onClick={() => document.execCommand('italic')} style={{ marginRight: '0.5rem', padding: '0.25rem 0.5rem' }}>I</button>
                                                    <button type="button" onClick={() => document.execCommand('underline')} style={{ marginRight: '0.5rem', padding: '0.25rem 0.5rem' }}>U</button>
                                                    <button type="button" onClick={() => document.execCommand('insertUnorderedList')} style={{ marginRight: '0.5rem', padding: '0.25rem 0.5rem' }}>• List</button>
                                                    <button type="button" onClick={() => document.execCommand('insertOrderedList')} style={{ marginRight: '0.5rem', padding: '0.25rem 0.5rem' }}>1. List</button>
                                                    <button type="button" onClick={() => document.execCommand('justifyLeft')} style={{ marginRight: '0.5rem', padding: '0.25rem 0.5rem' }}>⬅</button>
                                                    <button type="button" onClick={() => document.execCommand('justifyCenter')} style={{ marginRight: '0.5rem', padding: '0.25rem 0.5rem' }}>⬌</button>
                                                    <button type="button" onClick={() => document.execCommand('justifyRight')} style={{ marginRight: '0.5rem', padding: '0.25rem 0.5rem' }}>➡</button>

                                                    <button type="button" onClick={() => document.execCommand('foreColor', false, '#000000')} style={{ marginRight: '0.25rem', padding: '0.25rem', backgroundColor: '#000000', width: '25px', height: '25px', border: '1px solid #ccc' }}></button>
                                                    <button type="button" onClick={() => document.execCommand('foreColor', false, '#dc3545')} style={{ marginRight: '0.25rem', padding: '0.25rem', backgroundColor: '#dc3545', width: '25px', height: '25px', border: '1px solid #ccc' }}></button>
                                                    <button type="button" onClick={() => document.execCommand('foreColor', false, '#0d6efd')} style={{ marginRight: '0.25rem', padding: '0.25rem', backgroundColor: '#0d6efd', width: '25px', height: '25px', border: '1px solid #ccc' }}></button>
                                                    <button type="button" onClick={() => document.execCommand('foreColor', false, '#198754')} style={{ marginRight: '0.5rem', padding: '0.25rem', backgroundColor: '#198754', width: '25px', height: '25px', border: '1px solid #ccc' }}></button>

                                                    <input type="file" accept="image/*" onChange={handleImageUpload} style={{ marginRight: '0.5rem' }} />
                                                </div>

                                                <div
                                                    contentEditable={true}
                                                    onInput={(e) => setContent(e.currentTarget.innerHTML)}
                                                    ref={(el) => {
                                                        if (el && content && el.innerHTML !== content) {
                                                            el.innerHTML = content;
                                                        }
                                                    }}
                                                    style={{
                                                        minHeight: '300px',
                                                        border: '1px solid #ced4da',
                                                        borderRadius: '0 0 0.375rem 0.375rem',
                                                        padding: '0.75rem',
                                                        fontSize: '16px',
                                                        lineHeight: '1.5'
                                                    }}
                                                    className="form-control"
                                                    placeholder="Blog içeriğinizi yazın..."
                                                />
                                            </Form.Group>

                                            {/* Etiketler */}
                                            <Form.Group className="mb-3">
                                                <Form.Label>Etiketler</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="tags"
                                                    value={formData.tags}
                                                    onChange={handleInputChange}
                                                    placeholder="motor, bakım, ipucu (virgülle ayırın)"
                                                />
                                                <Form.Text className="text-muted">
                                                    Etiketleri virgülle ayırın (Opsiyonel)
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>

                                        {/* Sağ Kolon - Ayarlar */}
                                        <Col lg={4}>
                                            {/* Kategori */}
                                            <Form.Group className="mb-3">
                                                <Form.Label>Kategori *</Form.Label>
                                                <Form.Select
                                                    name="category"
                                                    value={formData.category}
                                                    onChange={handleInputChange}
                                                    isInvalid={!!validationErrors.category}
                                                >
                                                    <option value="">Kategori seçin...</option>
                                                    {categories.map(cat => (
                                                        <option key={cat.value} value={cat.value}>
                                                            {cat.label}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                <Form.Control.Feedback type="invalid">
                                                    {validationErrors.category}
                                                </Form.Control.Feedback>
                                            </Form.Group>

                                            {/* Durum */}
                                            <Form.Group className="mb-3">
                                                <Form.Label>Durum</Form.Label>
                                                <Form.Select
                                                    name="status"
                                                    value={formData.status}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="draft">Taslak</option>
                                                    <option value="published">Yayınlanmış</option>
                                                    <option value="archived">Arşivlenmiş</option>
                                                </Form.Select>
                                            </Form.Group>


                                            {/* Featured Image Upload */}
                                            <Form.Group className="mb-3">
                                                <Form.Label>Öne Çıkan Resim</Form.Label>
                                                <Form.Control
                                                    type="file"
                                                    accept="image/*"
                                                    name="images"
                                                />
                                                <Form.Text className="text-muted">
                                                    Blog listesinde görünecek ana resim
                                                </Form.Text>
                                            </Form.Group>

                                            {/* Admin Ayarları */}
                                            {currentUser.userType === 'admin' && (
                                                <div>
                                                    <h6 className="mb-3">Admin Ayarları</h6>
                                                    <Form.Group className="mb-3">
                                                        <Form.Check
                                                            type="checkbox"
                                                            id="isFeatured"
                                                            name="isFeatured"
                                                            label="Öne Çıkan"
                                                            checked={formData.isFeatured}
                                                            onChange={handleInputChange}
                                                        />
                                                    </Form.Group>
                                                    <Form.Group className="mb-3">
                                                        <Form.Check
                                                            type="checkbox"
                                                            id="isSticky"
                                                            name="isSticky"
                                                            label="Sabit (En üstte göster)"
                                                            checked={formData.isSticky}
                                                            onChange={handleInputChange}
                                                        />
                                                    </Form.Group>
                                                </div>
                                            )}
                                        </Col>
                                    </Row>

                                    {/* Action Buttons */}
                                    <hr />
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <Button
                                                variant="outline-primary"
                                                onClick={() => setShowPreview(true)}
                                                disabled={!formData.title || !content}
                                            >
                                                <FaEye className="me-2" />
                                                Önizleme
                                            </Button>
                                        </div>
                                        <div>
                                            <Button
                                                variant="outline-secondary"
                                                onClick={(e) => handleSubmit(e, true)}
                                                disabled={saving}
                                                className="me-2"
                                            >
                                                Taslak Olarak Kaydet
                                            </Button>
                                            <Button
                                                variant="dark"
                                                type="submit"
                                                disabled={saving}
                                            >
                                                <FaSave className="me-2" />
                                                {saving ? 'Güncelleniyor...' : 'Güncelle'}
                                            </Button>
                                        </div>
                                    </div>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Modals */}
            <PreviewModal />
            <DeleteConfirmModal />
        </div>
    );
};

export default EditBlogPage;